import React, { useState } from 'react';
import { Search } from "lucide-react";

// Reusable Tutorial Card with Hover Animations
const TutorialCard = ({ title, description, icon, iconBg, link }) => (
  <div className="group relative bg-white/70 dark:bg-slate-900/50 backdrop-blur-xl p-8 rounded-[24px] border border-slate-200/50 dark:border-slate-700/50 shadow-xl transition-all duration-500 hover:-translate-y-3 hover:shadow-2xl hover:shadow-primary/20 flex flex-col h-full overflow-hidden">
    
    {/* Animated Border Glow on Hover */}
    <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

    {/* Icon with Floating Effect */}
    <div className={`relative w-14 h-14 ${iconBg} rounded-2xl flex items-center justify-center text-2xl mb-6 shadow-lg transform transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6`}>
      {icon}
      <div className="absolute inset-0 rounded-2xl bg-current opacity-20 animate-pulse" />
    </div>
    
    <h3 className="relative text-xl font-bold text-slate-900 dark:text-white mb-3 group-hover:text-primary transition-colors duration-300">
      {title}
    </h3>
    
    <p className="relative text-slate-600 dark:text-slate-400 text-[14px] leading-relaxed mb-8 flex-grow">
      {description}
    </p>
    
    {/* Professional Button Style */}
    <a 
      href={`/tutorials/${title.toLowerCase().includes('excel') ? 'microsoft-excel-2021' : (title.toLowerCase().includes('word') ? 'microsoft-word-2021' : (title.toLowerCase().includes('devops') ? 'devops' : (title.toLowerCase().includes('ml & data science') ? 'ml-data-science' : (title.toLowerCase() === 'php' ? 'php' : (title.toLowerCase().includes('c++') ? 'cpp-programming' : (title.toLowerCase().includes('c programming') ? 'c-programming' : (title.toLowerCase().includes('java') && !title.toLowerCase().includes('script') ? 'java' : (title.toLowerCase().includes('javascript') ? 'javascript' : (title.toLowerCase().includes('python') ? 'python' : (title.toLowerCase().includes('algorithm') ? 'algorithms' : 'dsa'))))))))))}`}
      className="relative inline-flex items-center gap-2 text-primary font-bold text-sm group/btn"
    >
      <span className="bg-primary/10 py-2 px-4 rounded-full transition-all duration-300 group-hover/btn:bg-primary group-hover/btn:text-white">
        Start Learning
      </span>
      <span className="transform transition-transform duration-300 group-hover/btn:translate-x-2">
        →
      </span>
    </a>
  </div>
);

const Tutorials = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const tutorialData = [
    { title: "Data Structures", description: "Master the building blocks of software. From memory-efficient Arrays to complex Graphs and Trees.", icon: "📂", iconBg: "bg-purple-100 text-purple-600", link: "#" },
    { title: "Algorithms", description: "Level up your problem-solving. Covering sorting, searching, DP, and machine learning logic.", icon: "⚡", iconBg: "bg-orange-100 text-orange-600", link: "#" },
    { title: "JavaScript", description: "The heartbeat of the web. Modern ES6+, Async/Await, and core engine concepts for pro devs.", icon: "🌐", iconBg: "bg-yellow-100 text-yellow-600", link: "#" },
    { title: "Python", description: "Versatile and powerful. Deep dive into AI, Automation, Web Backend, and 2025's best practices.", icon: "🐍", iconBg: "bg-sky-100 text-sky-600", link: "#" },
    { title: "Java", description: "Enterprise-grade programming. From JVM internals to Spring Boot and modern Virtual Threads.", icon: "☕", iconBg: "bg-red-100 text-red-600", link: "#" },
    { title: "C Programming", description: "The foundation of computing. Understand memory management and low-level system design.", icon: "⚙️", iconBg: "bg-blue-100 text-blue-600", link: "#" },
    { title: "C++ Programming", description: "High-performance systems. STL, OOP, and efficiency techniques for competitive coding.", icon: "💎", iconBg: "bg-indigo-100 text-indigo-600", link: "#" },
    { title: "PHP", description: "The engine of the web. Build secure, scalable server-side applications and CMS systems.", icon: "🐘", iconBg: "bg-violet-100 text-violet-600", link: "#" },
    { title: "ML & Data Science", description: "The future is here. Neural Networks, Predictive Modeling, MLOps, and Data Engineering.", icon: "🧠", iconBg: "bg-teal-100 text-teal-600", link: "#" },
    { title: "DevOps", description: "Cloud-native mastery. CI/CD pipelines, Docker, Kubernetes, and Infrastructure as Code.", icon: "☁️", iconBg: "bg-cyan-100 text-cyan-600", link: "#" },
    { title: "MS Word 2021", description: "Professional documentation. Master formatting, macros, and collaborative tools.", icon: "📝", iconBg: "bg-blue-100 text-blue-700", link: "#" },
    { title: "MS Excel 2021", description: "Data analysis power. Advanced formulas, Power Query, and stunning Visualizations.", icon: "📊", iconBg: "bg-green-100 text-green-600", link: "#" },
  ];

  return (
    <main className="relative pt-[160px] pb-24 px-5 bg-slate-50 dark:bg-slate-950 min-h-screen overflow-hidden">
      
      {/* Background Decorative Elements (Blobs) */}
      <div className="absolute top-0 -left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] pointer-events-none animate-pulse" />
      <div className="absolute bottom-0 -right-20 w-96 h-96 bg-purple-500/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-[1300px] mx-auto relative z-10">
        
        {/* Header Section */}
        <div className="text-center mb-16 space-y-4">
          <span className="inline-block py-1 px-4 rounded-full bg-primary/10 text-primary text-xs font-bold tracking-widest uppercase">
            Knowledge Hub
          </span>
          <h1 className="text-5xl md:text-7xl font-black tracking-tight text-slate-900 dark:text-white">
            Learn to <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-500">Code</span>
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Premium, industry-aligned tutorials curated by 
            <span className="font-bold text-primary"> Senvra Ai</span>. 
            From fundamental logic to advanced architecture.
          </p>
        </div>

        {/* Professional Search Bar (Glassmorphism) */}
        <div className="max-w-2xl mx-auto mb-20">
          <div className="group relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary to-purple-600 rounded-[22px] blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
            <div className="relative flex items-center bg-white dark:bg-slate-900 rounded-[20px] shadow-sm">
              <div className="pl-6 text-slate-400">
                <Search className="w-6 h-6" />
              </div>
              <input
                type="text"
                placeholder="What do you want to master today?"
                className="w-full py-5 px-4 bg-transparent outline-none text-slate-700 dark:text-slate-200 font-medium placeholder:text-slate-400"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="pr-4 hidden md:block">
                {/*<kbd className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md">
                  Ctrl + K
                </kbd>*/}
              </div>
            </div>
          </div>
        </div>

        {/* Grid Section - Responsive Columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {tutorialData
            .filter(item => item.title.toLowerCase().includes(searchQuery.toLowerCase()))
            .map((item, index) => (
              <TutorialCard key={index} {...item} />
            ))
          }
        </div>

        {/* Bottom CTA or Info */}
        <div className="mt-20 text-center">
          <p className="text-slate-500 dark:text-slate-500 text-sm italic">
            Can't find what you're looking for? New tutorials are added every week.
          </p>
        </div>
      </div>
    </main>
  );
};

export default Tutorials;